/**
 * Fire-and-forget adbi event tracker.
 * Never throws, never blocks the caller.
 */
import async from 'async'
import debug from 'debug'
import { config } from 'src/utils/config'
import { safeUUID } from 'src/utils/genId'
import { getAcquisitionData, getOrCreateAnonymousId } from './adbiAttribution'

const log = debug('pixai:adbi:tracker')
const ADBI_FETCH_TIMEOUT_MS = 5_000

export function trackAdbi(
  eventName: string,
  userId: string | null,
  properties: Record<string, unknown> = {},
): void {
  if (typeof window === 'undefined') return
  if (!config.adbiUrl) return

  const anonymous_id = getOrCreateAnonymousId()

  const payload = {
    event_id: safeUUID(),
    anonymous_id: anonymous_id || null,
    user_id: userId ?? null,
    event_name: eventName,
    timestamp: new Date().toISOString(),
    platform: 'web',
    properties,
    context: {
      user_agent: navigator.userAgent,
      page_url: window.location.href,
      referrer: document.referrer || undefined,
    },
  }

  log('tracking event: %s user_id=%s', eventName, userId ?? 'anon')
  // Retry once with the same event_id for idempotency
  const body = JSON.stringify(payload)
  void async.retry({ times: 2, interval: 0 }, async () => {
    const res = await fetch(`${config.adbiUrl}/ingest/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: AbortSignal.timeout(ADBI_FETCH_TIMEOUT_MS),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
  })
}

/**
 * Sends $identify to bind anonymous_id → user_id in adbi.
 * Also attaches any stored acquisition data (UTM / click params).
 */
export function adbiIdentify(userId: string): void {
  const acquisition = getAcquisitionData()
  trackAdbi('$identify', userId, acquisition ?? {})
}
